import type { ITeacherDetails } from '@/types/teacher';

import { IconMail, IconPhone } from '@tabler/icons-react';

import CustomImage from '@/components/CustomImage';
import { useFormatText } from '@/hooks/useFormatText';
import { getAssetUrl } from '@/utils';

interface ITeacherProfileProps {
  teacher: ITeacherDetails;
}

const TeacherProfile = ({ teacher }: ITeacherProfileProps) => {
  const { formatText } = useFormatText();

  const initials = teacher.teacher_name
    .trim()
    .split(' ')
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');

  return (
    <div
      className="flex flex-col gap-4"
      data-testid="teacher-profile-container"
    >
      <div className="flex gap-4 items-center">
        {/* Avatar */}
        <div
          className="w-25 h-25 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden"
          data-testid="teacher-avatar"
        >
          {getAssetUrl(teacher?.profile_pic) ? (
            <CustomImage
              src={getAssetUrl(teacher.profile_pic)}
              alt={teacher.teacher_name}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-4xl font-bold text-gray-500">{initials}</span>
          )}
        </div>

        {/* Name */}
        <h2
          className="text-2xl font-bold text-neutral-primary leading-8 tracking-[-0.24px]"
          data-testid="teacher-name"
        >
          {teacher.teacher_name}
        </h2>
      </div>

      {/* Contact Info */}
      <div className="flex items-start gap-6">
        <div className="flex items-start gap-2 " data-testid="teacher-email">
          <IconMail size={20} className="text-neutral-primary mt-0.5" />
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral-secondary leading-5">
              {formatText('teacher-details.email')}
            </span>
            <span className="text-base text-neutral-primary leading-6">
              {teacher.email}
            </span>
          </div>
        </div>

        <div className="flex items-start gap-2 " data-testid="teacher-phone">
          <IconPhone size={20} className="text-neutral-primary mt-0.5" />
          <div className="flex flex-col gap-1">
            <span className="text-sm text-neutral-secondary leading-5">
              {formatText('teacher-details.phone')}
            </span>
            <span className="text-base text-neutral-primary leading-6">
              {teacher.phone_code} {teacher.phone_number}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherProfile;
